import type { AnswerMap, QUIZ_TYPE } from '@models/quiz';
import { useQuizStore } from '@stores/quiz';
import { useUserStore } from '@stores/user';
import { useLocalStorage } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, watch } from 'vue';

export const useQuizHistory = (quizType: QUIZ_TYPE) => {
  const userStore = useUserStore();
  const isGuest = computed(() => userStore.info?.isGuest ?? true);

  const quizStore = useQuizStore();
  const { counter, answers, answerArray } = storeToRefs(quizStore);
  const { forceAnswer } = quizStore;

  const quizHistory = useLocalStorage<{
    counter: number;
    answers: AnswerMap;
  }>(quizType, {
    counter: 0,
    answers: {},
  });

  if (isGuest.value && answerArray.value.length === 0) {
    counter.value = quizHistory.value.counter;
    answers.value = quizHistory.value.answers;
    forceAnswer();
  }

  const clearQuizHistory = () => {
    quizHistory.value.counter = 0;
    quizHistory.value.answers = {};
  };

  const unwatchQuiz = watch([counter, answers], ([counter, answers]) => {
    if (!isGuest.value) {
      return;
    }
    quizHistory.value.counter = counter;
    quizHistory.value.answers = answers;
  });

  watch(
    quizHistory,
    () => {
      if (!isGuest.value) {
        return;
      }
      counter.value = quizHistory.value.counter;
      answers.value = quizHistory.value.answers;
    },
    {
      immediate: true,
      once: true,
    },
  );

  watch(
    isGuest,
    (isGuest) => {
      if (isGuest) {
        return;
      }
      unwatchQuiz();
      clearQuizHistory();
    },
    {
      immediate: true,
    },
  );

  return {
    quizHistory,
    clearQuizHistory,
    unwatchQuiz,
  };
};
